import type { AsyncActionHandlers } from 'use-reducer-async';
import find from 'lodash/find';
import get from 'lodash/get';
import produce from 'immer';
import has from 'lodash/has';
import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import {
  addCheckboxField,
  addDescriptionField,
  addEmailField,
  addMetadataField,
  addRadioField,
  addUploadField,
} from '../../../../new-file-request-builder/FormElementMaker';
import type { BuilderReducerType } from '../../builder-type';
import {
  ADD_REMOVE_FIELD_ACTIONS,
  type AddRemoveFieldHandlerActions,
} from './add-remove-field-action-type';
import selector from '../../builder-selector';
import { ACTION_SET_FORM_ELEMENT_TESTIDS } from '../../builder-constant';
import { removeFieldFromFormContent } from '../builder-action.utils';

export const addRemoveFieldActionHandlers = (): AsyncActionHandlers<
  BuilderReducerType,
  AddRemoveFieldHandlerActions
> => {
  return {
    [ADD_REMOVE_FIELD_ACTIONS.ADD_CHECKBOX_FIELD]:
      (dispatch, getState) => async action => {
        const state = getState();
        let formContent = selector.getFormContent(state);
        formContent = addCheckboxField(formContent);
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formbuilder',
          target: 'addcheckbox',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch({
          type: ADD_REMOVE_FIELD_ACTIONS.UPDATE_CONTENT,
          formContent,
        });
        dispatch({ ...action });
      },
    [ADD_REMOVE_FIELD_ACTIONS.ADD_DESCRIPTION_FIELD]:
      (dispatch, getState) => async action => {
        const { intl } = action;
        const state = getState();
        let formContent = selector.getFormContent(state);
        formContent = addDescriptionField(formContent, intl);
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formbuilder',
          target: 'addfiledescription',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch({
          type: ADD_REMOVE_FIELD_ACTIONS.UPDATE_CONTENT,
          formContent,
        });
        dispatch({ ...action });
      },

    [ADD_REMOVE_FIELD_ACTIONS.ADD_EMAIL_FIELD]:
      (dispatch, getState) => async action => {
        const { intl } = action;
        const state = getState();
        let formContent = selector.getFormContent(state);
        const formType = get(state, 'workingFileRequest.form.formType');
        formContent = addEmailField(formContent, intl, formType);
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formbuilder',
          target: 'addemail',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch({
          type: ADD_REMOVE_FIELD_ACTIONS.UPDATE_CONTENT,
          formContent,
        });
        dispatch({ ...action });
      },

    [ADD_REMOVE_FIELD_ACTIONS.ADD_METADATA_FIELD]:
      (dispatch, getState) => async action => {
        const state = getState();
        const { metadataField, metadataTemplateId } = action;
        let formContent = selector.getFormContent(state);
        formContent = addMetadataField(
          metadataField,
          metadataTemplateId,
          formContent,
        );
        const newMetadataTemplate = produce(
          selector.getActiveMetadataTemplate(state),
          newTemplate => {
            const field = find(get(newTemplate, 'fields', []), [
              'key',
              metadataField?.key,
            ]);
            if (field) field.isSelected = true;
          },
        );
        dispatch({
          type: ADD_REMOVE_FIELD_ACTIONS.UPDATE_CONTENT,
          formContent,
        });
        dispatch({ ...action, newMetadataTemplate });
      },

    [ADD_REMOVE_FIELD_ACTIONS.ADD_RADIO_FIELD]:
      (dispatch, getState) => async action => {
        const state = getState();
        let formContent = selector.getFormContent(state);
        formContent = addRadioField(formContent);
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formbuilder',
          target: 'addradio',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch({
          type: ADD_REMOVE_FIELD_ACTIONS.UPDATE_CONTENT,
          formContent,
        });
        dispatch({ ...action });
      },

    [ADD_REMOVE_FIELD_ACTIONS.ADD_UPLOAD_FIELD]:
      (dispatch, getState) => async action => {
        const { intl, mode } = action;
        const state = getState();
        let formContent = selector.getFormContent(state);
        formContent = addUploadField(formContent, intl, mode);
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formbuilder',
          target: 'adduploadzone',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch({
          type: ADD_REMOVE_FIELD_ACTIONS.UPDATE_CONTENT,
          formContent,
        });
        dispatch({ ...action });
      },

    [ADD_REMOVE_FIELD_ACTIONS.REMOVE_FIELD]:
      (dispatch, getState) => async action => {
        const { id, intl, multipleMetadataTemplatesEnabled } = action;

        if (!id || !intl) {
          return;
        }

        const state = getState();

        // If metadata field, unselect field
        let newMetadataTemplates = cloneDeep(
          selector.getActiveMetadataTemplates(state),
        );

        const component = selector.getComponentForId(state, id);
        if (!component) {
          return;
        }

        if (
          newMetadataTemplates?.length > 0 &&
          has(component, 'metadataFieldKey') &&
          has(component, 'metadataTemplateId')
        ) {
          // find the template for field to remove and set isSelected false for the fields
          const templateToUpdate = newMetadataTemplates.find(
            tempalte => tempalte.id === component.metadataTemplateId,
          );

          if (templateToUpdate) {
            const field = find(templateToUpdate.fields, [
              'key',
              component.metadataFieldKey,
            ]);
            if (field) field.isSelected = false;
            if (
              templateToUpdate.fields.every(({ isSelected }) => !isSelected)
            ) {
              // remove template if all the fields in this template is unselected
              newMetadataTemplates = newMetadataTemplates.filter(
                templateEl => templateEl.id !== templateToUpdate.id,
              );
            }
          }
        }
        // Update testids
        const formElementTestids = omit(state.formElementTestids, [id]);

        const formContent = removeFieldFromFormContent(state, component, intl);

        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formelement',
          target: `remove`,
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch({
          type: ADD_REMOVE_FIELD_ACTIONS.REMOVE_FIELD,
          formContent,
          newMetadataTemplates,
          multipleMetadataTemplatesEnabled,
        });
        dispatch({ type: ACTION_SET_FORM_ELEMENT_TESTIDS, formElementTestids });
      },
  };
};
